import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FieldArray, useFormikContext } from "formik";
import { v4 } from "uuid";
import { getIndexedBlock } from "@reside/forms/dist/BlockFieldArray.helpers";
import { styled } from "../../theme";
import { FieldErrorText } from "../../form/atoms/field-error";
import { Button } from "../../button";
import { Text } from "../../text";
import { FlexRow } from "../../box";
import { ValidationBlockBoundary } from "../validation-block-boundary";
import { BlockField, useBlockComponentContext } from "../";
import { CollapsibleItems } from "./atoms/collapsible-items";
export const BlockFieldArray = ({ id, translationKey, emptyMessage, collapsible, headlineLabel, children, queryVariables, onCatch, }) => {
    const { Translated, Markdown, translate } = useBlockComponentContext();
    const { values, errors, touched } = useFormikContext();
    const items = values[id] || [];
    return (_jsx(ValidationBlockBoundary, { name: id, children: _jsx(FieldArray, { name: id, children: arrayHelpers => (_jsxs(_Fragment, { children: [collapsible ? (_jsx(CollapsibleItems, { items: items, id: id, translationKey: translationKey, headlineLabel: headlineLabel, onRemove: index => arrayHelpers.remove(index), children: index => (_jsx(Body, { children: children.map(block => (_jsx(BlockField, { block: getIndexedBlock(block, id, index, {
                                    ...items[index],
                                    ...values,
                                }), queryVariables: queryVariables, onCatch: onCatch }, block.id))) })) })) : (items.map((item, index) => {
                        return (_jsx(Container, { children: _jsxs(Body, { children: [_jsxs(Header, { children: [_jsxs(ItemLabel, { children: [translate(translationKey), " #", index + 1] }), _jsx(AlignRight, { children: _jsx(RemoveButton, { translationKey: translationKey, onClick: () => {
                                                        arrayHelpers.remove(index);
                                                    } }) })] }), children.map(block => (_jsx(BlockField, { block: getIndexedBlock(block, id, index, {
                                            ...item,
                                            ...values,
                                        }), queryVariables: queryVariables, onCatch: onCatch }, block.id)))] }) }, index));
                    })), emptyMessage && items.length === 0 && (_jsx(EmptyMessage, { children: _jsx(Translated, { translationKey: emptyMessage.translationKey, render: ({ translation }) => _jsx(Markdown, { source: translation }) }) })), _jsx(AddButton, { translationKey: translationKey, onClick: () => {
                            arrayHelpers.push({ id: v4() });
                        } }), _jsx(FieldArrayError, { touch: touched[id], error: errors[id] })] })) }) }));
};
const FieldArrayError = ({ touch, error }) => touch && typeof error === "string" ? (_jsx(FieldErrorText, { children: error })) : null;
const Container = styled.div `
  margin-bottom: 1em;
  border: 1px solid ${({ theme }) => theme.color.gray10};
`;
const Body = styled.div `
  padding: 1em 20px;
`;
const Header = styled(FlexRow) `
  align-items: center;
`;
export const AlignRight = styled.div `
  display: flex;
  justify-content: flex-end;
`;
export const ItemLabel = styled((props) => (_jsx(Text, { as: "div", color: "gray100", ...props }))) `
  text-transform: uppercase;
`;
export const RemoveButton = ({ translationKey, ...props }) => {
    const { Translated } = useBlockComponentContext();
    return (_jsxs(Button, { ...props, outline: true, color: "danger", children: ["Remove ", _jsx(Translated, { translationKey: translationKey })] }));
};
const AddButton = ({ translationKey, ...props }) => {
    const { Translated } = useBlockComponentContext();
    return (_jsxs(Button, { ...props, outline: true, color: "primary", children: ["Add ", _jsx(Translated, { translationKey: translationKey })] }));
};
const EmptyMessage = styled.p `
  margin-bottom: 15px;
`;
